import { bottom } from "@popperjs/core";
import { inject, observer } from "mobx-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { Avatar, Paper, Text, Tooltip } from "src/components";
import { theme } from "src/config";
import { Question } from "src/models";
import { AuthStore, QuestionStore, UserStore, SpaceStore } from "src/stores";
import { ItemStatus } from "src/types";
import styled from "styled-components";

export interface IQuestionBoardProps {
  questionStore?: QuestionStore;
  userStore?:     UserStore;
  authStore?:     AuthStore;
  spaceStore?:    SpaceStore;
}

export const QuestionBoard = inject("questionStore", "userStore", "authStore", "spaceStore")(observer((props: IQuestionBoardProps) => {
  const { questionStore, userStore, authStore, spaceStore } = props;
  const { t, i18n } = useTranslation();

  const users = userStore.getUsersBySpace(authStore.getCurRoomID, "all").filter( u => !u.getCurSpaceSettings.hide_in_matrix);

  const questions: { [from: number]: { [to: number]: Question[] } } =
    React.useMemo(() => questionStore.storage.board.getArray.reduce(
        (
          res: { [from: number]: { [to: number]: Question[] } },
          val: Question
        ) => {
          res[val.from] = res[val.from] || {};
          res[val.from][val.to] = res[val.from][val.to] || [];
          res[val.from][val.to].push(val);
          return res;
        },
        {}
      ),
    [users, questionStore.storage.board.getArray]
  );

  const headers = React.useMemo(() => users.map((u) => (
    <th key={u.id}>
      <InnerCell onClick={() => {
        questionStore.setFilterValue("to", [{key: u.id, label: u.getFullName, value: u}], true);
        questionStore.updateQuery();
        questionStore.advSearch = true;
        }}>
        <Tooltip 
          placement="bottom"
          content={t("view_all_questions_assigned") + " " + u.getFullName}>
            <Avatar
              initials        = {u.getInitials}
              size            = "medium"
              backgroundColor = {theme.color.grayDark}
            />
        </Tooltip>
      </InnerCell>
    </th>
  )), [users]);

  const rows = React.useMemo(
    () =>
      users.map((from) => (
        <TableRow key={from.id}>
          <th>
            <Tooltip
              content={t("view_all_questions_asked") + " " + from.getFullName}
            >
              <InnerCell onClick={() => {
                questionStore.setFilterValue("from", [{key: from.id, label: from.getFullName, value: from}], true);
                questionStore.updateQuery();
                questionStore.advSearch = true;
                }}>
                  <Avatar
                    initials        = {from.getInitials}
                    size            = "medium"
                    backgroundColor = {theme.color.grayDark}
                  />
                <Text size="small" weight="normal" color="grayDark">
                  {spaceStore?.getCurRoom?.member_listing === "title" ? from.getTitle : from.getFullName}
                </Text>
              </InnerCell>
            </Tooltip>
          </th>
          {users.map((to) => (
            <td key={to.id}>
              <Tooltip
                content={
                  t("view_all_questions_from") +
                  " " +
                  from.getFullName +
                  " " +
                  t("to") +
                  " " +
                  to.getFullName
                }
              >
                <InnerCell 
                  onClick={() => {
                    questionStore.setFilterValue("from", [{key: from.id, label: from.getFullName, value: from}], true); 
                    questionStore.setFilterValue("to",   [{key: to.id,   label: to.getFullName,   value: to}],   false); 
                    questionStore.updateQuery();
                    questionStore.advSearch = true;
                  }}
                >
                  {questions[from.id] && questions[from.id][to.id] && (
                    <OnBoardQuestion>
                      <QuestionCounter>
                        {questions[from.id][to.id].length} {"st"}
                      </QuestionCounter>
                      <StatusCounterContainer>
                        {questions[from.id][to.id].filter(q => q.getStatus === ItemStatus.Answered).length > 0 &&
                        <StatusCounter variant="answered">{questions[from.id][to.id].filter(q => q.getStatus === ItemStatus.Answered).length}</StatusCounter>
                        }

                        {questions[from.id][to.id].filter(q => q.getStatus === ItemStatus.Critical).length > 0 &&
                        <StatusCounter variant="critical">{questions[from.id][to.id].filter(q => q.getStatus === ItemStatus.Critical).length}</StatusCounter>
                        }
                      </StatusCounterContainer>
                    </OnBoardQuestion>
                  )}
                </InnerCell>
              </Tooltip>
            </td>
          ))}
        </TableRow>
      )),
    [users, questions]
  );

  return (
    <QuestionBoardContainer>
    <StyledPaper
      $center
      style       = {{ ...theme.paper.fullwidth}}
      $padding    = "0rem"
      $marginLeft = "2rem"
      $noShadow
    >
      <StyledTable>
        <thead>
          <tr>
            <th style={{ verticalAlign: bottom}} onClick={() => {}}>
              <Tooltip content={t("view_all_questions_onboard")}>
                <ToContainerWrapper>
                  <ToContainer>
                    <Text color="grayDark" size="large" as="span">
                      {t("to")}
                    </Text>
                  </ToContainer>
                </ToContainerWrapper>
                  <Text color="grayDark" size="large" as="span">
                    {t("from")}
                  </Text>
              </Tooltip>
            </th>
            {headers}
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </StyledTable>
    </StyledPaper>
    </QuestionBoardContainer>
  );
}));

const StyledPaper = styled(Paper)`
  width:         fit-content;
  margin-bottom: 1rem;
`;

const QuestionBoardContainer = styled.div`
  overflow: auto;
  padding:  0.5rem 0.5rem; 
`

const StyledTable = styled.table`
  td,
  th {
    min-width:      12rem;
    height:         10rem;
    padding:        2rem;
    align-items:    center;
    vertical-align: middle;
    padding:        1rem;
  }

  td {
    text-align:  center;
    align-items: center;
    border-left: 1px solid ${theme.color.primaryBorder};

    :hover {
      background-color: ${theme.color.hover};
    }
  }

  th {
    border-right: 1px solid ${theme.color.primaryBorder};
    align-items:  center;
    cursor:       pointer;

    :last-of-type {
      border-right: none;
    }

    :hover {
      background-color: ${theme.color.hover};
    }
  }
`;

const ToContainer = styled.div`
  transform:       rotate(270deg);
  text-transform:  capitalize;
  justify-content: flex-end;
  display:         flex;
`;

const ToContainerWrapper = styled.div`
  justify-content: flex-end;
  display:         flex;
`;

const OnBoardQuestion = styled.div`
  min-width:      8rem;
  min-height:     8rem;
  display:        flex;
  flex-direction: column;
  padding:        1rem;
  width:          8rem;
  height:         fit-content;
  border-radius:  0.3rem;
  font-size:      ${theme.font.size.small};
  font-weight:    ${theme.font.weight.bold};
  background:     ${theme.color.cards.question};
  box-shadow:     ${theme.boxShadow.small};
  cursor:         pointer;
`;

const StatusCounter = styled.div<{variant?: "answered" | "critical"; }>`
  border-radius: 50%;
  padding:       0.2rem;
  min-width:     2rem;
  min-height:    2rem;
  margin:        0.3rem;
  background:    ${(p) => p.variant === "answered" ? theme.color.status.answered : theme.color.status.critical };
  font-size:     ${theme.font.size.xsmall};
  color:         white;
  text-align:    center;
`;

const QuestionCounter = styled(StatusCounter)`
  background:   none;
  color:        black;
  font-weight:  ${theme.font.weight.normal};
  font-size:    ${theme.font.size.small};
  margin-right: 0.2rem;
`;

const StatusCounterContainer = styled.div`
  display:         flex;
  flex-wrap:       wrap;
  justify-content: center;
  `

const InnerCell = styled.div`
  display:         flex;
  flex-direction:  column;
  justify-content: center;
  align-items:     center;
  margin:          auto;
`;

const TableRow = styled.tr`
  border-top:   1px solid ${theme.color.primaryBorder};
  border-right: none;
`;
